/**
 * Visibility: conditionals + hide empty screens/stages.
 * Only answers from currently visible questions drive dependents (chained hide).
 */
import { isQuestionVisible } from "./evaluateConditional";
import { collectQuestions } from "./composition";

function compositionParentIds(composition) {
  const ids = new Set();
  for (const question of collectQuestions(composition)) {
    if (question.canonicalQuestionId) ids.add(String(question.canonicalQuestionId));
    if (question.machineKey) ids.add(String(question.machineKey));
  }
  return ids;
}

/** Effective answers: exclude keys owned by questions that are not visible. */
function adoptAnswerKeys(question, map, effective) {
  let adopted = false;
  for (const key of [question.canonicalQuestionId, question.machineKey]) {
    if (!key || !Object.hasOwn(map, key) || Object.hasOwn(effective, key)) continue;
    effective[key] = map[key];
    adopted = true;
  }
  return adopted;
}

function answersForVisibleConditionals(composition, answersById) {
  const parentIds = compositionParentIds(composition);
  const questions = collectQuestions(composition);
  const map = answersById || {};
  const effective = {};
  let progress = true;

  while (progress) {
    progress = false;
    for (const question of questions) {
      if (!isQuestionVisible(question, effective, parentIds)) continue;
      if (adoptAnswerKeys(question, map, effective)) progress = true;
    }
  }
  return effective;
}

export function mergeAnswerMaps(...maps) {
  const out = {};
  for (const map of maps) {
    if (!map || typeof map !== "object") continue;
    Object.assign(out, map);
  }
  return out;
}

export function filterVisibleComposition(composition, answersById) {
  if (!composition?.stages) {
    return { stages: [] };
  }
  const parentIds = compositionParentIds(composition);
  const effectiveAnswers = answersForVisibleConditionals(composition, answersById);
  const stages = [];

  for (const stage of composition.stages) {
    const screens = [];
    for (const screen of stage.screens || []) {
      const questions = (screen.questions || []).filter((question) =>
        isQuestionVisible(question, effectiveAnswers, parentIds)
      );
      if (!questions.length) continue;
      screens.push({ ...screen, questions });
    }
    if (!screens.length) continue;
    stages.push({ ...stage, screens });
  }

  return { ...composition, stages };
}

export function collectVisibleQuestions(composition, answersById) {
  const filtered = filterVisibleComposition(composition, answersById);
  const questions = [];
  for (const stage of filtered.stages) {
    for (const screen of stage.screens) {
      questions.push(...screen.questions);
    }
  }
  return questions;
}
